import React, { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as ArrowUp } from "../../../../assets/icons/svg/arrow-up.svg";
import { ReactComponent as ArrowDown } from "../../../../assets/icons/svg/arrow-down.svg";
import { ReactComponent as UploadCreative } from "../../../../assets/icons/svg/upload-creative.svg";
import { ReactComponent as FileUpload } from "../../../../assets/icons/svg/file-upload.svg";
import { ReactComponent as ImageUpload } from "../../../../assets/icons/svg/file-uploading.svg";
import ItemListPopup from "../../../ui/ItemListPopup";
import CustomButton from "../../../ui/CommonButton";
import { useCreatives } from "../../../../context/CreativeContext";
import ToastContainer, { useToast } from "../../../ui/Toast_Message";
import { useTopBar } from "../../../../context/TopBarContext";
import CreativesCard from "../../../ui/CreativesCard";

// Define the orientation type
type OrientationType = "landscape" | "portrait";

const UploadCreativeScreen: React.FC = () => {
  const location1 = useLocation();
  const navigate = useNavigate();
  const { showToast, toasts } = useToast();
  const searchParams = new URLSearchParams(location1.search);
  const shouldOpenDirectly = searchParams.get("direct") === "true";
  const [showPopup, setShowPopup] = useState(shouldOpenDirectly);
  const { setTitle, setShowBackButton, pushNavState } = useTopBar();
  const location = useLocation();

  // Get creatives from context
  const { creatives } = useCreatives();

  const [creativeTitle, setCreativeTitle] = useState("");
  // Updated with proper typing - allow null instead of empty string
  const [orientation, setOrientation] = useState<OrientationType | null>(null);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [fileFormat, setFileFormat] = useState<string>("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(
    null
  );
  const [orientationError, setOrientationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { addCreative } = useCreatives();

  const orientationOptions = [
    { label: "Portrait", value: "portrait" as const, dimension: "1080 x 1920" },
    {
      label: "Landscape",
      value: "landscape" as const,
      dimension: "1920 x 1080",
    },
  ];

  const allowedFormats = ["jpg", "jpeg", "png", "mp4"];

  useEffect(() => {
    const title = "Creative";
    const showBack = false;

    setTitle(title);
    setShowBackButton(showBack);

    pushNavState({
      path: location.pathname,
      title,
      showBackButton: showBack,
    });
  }, [location.pathname]);

  const resetForm = () => {
    setCreativeTitle("");
    setOrientation(null); // Reset to null instead of empty string
    setUploadedImage(null);
    setFileFormat("");
    setDropdownOpen(false);
    setIsDragging(false);
    setUploadError(false);
    setUploadErrorMessage(null);
    setOrientationError(null);
  };

  const handleOpenPopup = () => setShowPopup(true);

  const handleCancel = () => {
    resetForm();
    setShowPopup(false);
    if (shouldOpenDirectly) {
      navigate("/creatives/list");
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    validateAndSetFile(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    validateAndSetFile(file);
  };

  const validateAndSetFile = (file?: File) => {
    if (!file) return;

    if (!orientation) {
      setOrientationError("Please select orientation before uploading.");
      setUploadErrorMessage(null);
      return;
    } else {
      setOrientationError(null);
    }

    const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";
    const isAllowedFormat = allowedFormats.includes(fileExtension);

    if (!isAllowedFormat) {
      setUploadedImage(null);
      setFileFormat("");
      setUploadErrorMessage(
        "Invalid file format. Allowed: jpg, jpeg, png, mp4."
      );
      return;
    }

    const maxFileSize = 20 * 1024 * 1024;
    if (file.size > maxFileSize) {
      setUploadedImage(null);
      setFileFormat("");
      setUploadErrorMessage("File size exceeds 20MB.");
      return;
    }

    const reader = new FileReader();

    if (fileExtension === "mp4") {
      reader.onloadend = () => {
        setUploadedImage(reader.result as string);
        setFileFormat(fileExtension);
        setUploadErrorMessage(null);
      };
      reader.readAsDataURL(file);
      return;
    }

    reader.onloadend = () => {
      const img = new Image();
      img.onload = () => {
        const width = img.width;
        const height = img.height;

        if (orientation === "portrait" && width >= height) {
          setUploadedImage(null);
          setFileFormat("");
          setUploadErrorMessage(
            "Image does not match portrait orientation (height > width)."
          );
          return;
        }

        if (orientation === "landscape" && width <= height) {
          setUploadedImage(null);
          setFileFormat("");
          setUploadErrorMessage(
            "Image does not match landscape orientation (width > height)."
          );
          return;
        }

        setUploadedImage(reader.result as string);
        setFileFormat(fileExtension);
        setUploadErrorMessage(null);
      };
      img.src = reader.result as string;
    };

    reader.readAsDataURL(file);
  };

  const handleUpload = () => {
    // TypeScript now knows orientation is not null due to the explicit check
    if (creativeTitle && orientation && uploadedImage && !uploadError) {
      addCreative({
        title: creativeTitle,
        orientation,
        imageUrl: uploadedImage,
        fileFormat,
      });

      showToast(
        "success",
        "",
        "Uploaded successfully!",
        `upload-success-${Date.now()}`
      );
      resetForm();
      setShowPopup(false);
      navigate("/creatives/list");
    }
  };

  const isFormValid =
    creativeTitle && orientation && uploadedImage && !uploadError;

  return (
    <>
      {/* Background Content - Always show creatives if they exist, otherwise show upload prompt */}
      <div className="min-h-screen bg-white py-4 pl-6 pr-2 mt-1">
        <div className="mx-auto">
          {/* Show creatives if they exist */}
          {creatives.length > 0 ? (
            <>
              {/* Top Bar */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-sm 2xl:text-base 4xl:text-lg font-medium text-gray-900">
                  Uploaded {creatives.length} creatives
                </h2>
              </div>

              {/* Creatives Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-4 3xl:grid-cols-4 4xl:grid-cols-5 gap-6 justify-items-center">
                {creatives.map((creative) => (
                  <CreativesCard key={creative.id} creative={creative} />
                ))}
              </div>
            </>
          ) : (
            /* Show upload prompt if no creatives exist */
            <div className="fixed inset-20 2xl:inset-40 overflow-hidden flex items-center justify-center bg-white">
              <div className="flex flex-col items-center text-center space-y-6">
                <UploadCreative className="w-44 h-44 text-gray-500 -mb-3 " />
                <div className="space-y-4">
                  <h2 className="text-2xl font-semibold text-gray-800 ">
                    Upload Creative
                  </h2>
                  <p className="text-sm text-gray-500 max-w-xs leading-relaxed ">
                    Easily upload all the content you'd like to display on your
                    screens here!
                  </p>
                </div>
                <button
                  onClick={handleOpenPopup}
                  className="inline-flex items-center justify-center mt-6 gap-2.5 w-48 h-12 px-4 bg-[#0BA6C7] hover:bg-[#0A99B9] text-white text-sm font-medium border border-[#0BA6C7] hover:border-[#0A99B9] rounded-xl transition-colors duration-200 active:bg-[#0991A8]"
                >
                  <FileUpload className="w-5 h-5" />
                  Upload Creative
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Popup Overlay */}
      {showPopup && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" />
          <ItemListPopup title="Upload Creative" onClose={handleCancel}>
            <div className="flex flex-col h-full ">
              <div className="overflow-y-auto pr-1 space-y-4 ">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Creative title
                  </label>
                  <input
                    type="text"
                    value={creativeTitle}
                    onChange={(e) => setCreativeTitle(e.target.value)}
                    placeholder="Enter creative title"
                    maxLength={32}
                    className="w-full px-3 py-2 border rounded-[8px] text-sm border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Choose orientation
                  </label>
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-[8px] text-left text-sm bg-white flex items-center justify-between focus:outline-none focus:ring-1 focus:ring-blue-400"
                  >
                    <span
                      className={orientation ? "text-black" : "text-gray-400"}
                    >
                      {orientationOptions.find((o) => o.value === orientation)
                        ?.label || "Select orientation"}
                    </span>
                    <span>{dropdownOpen ? <ArrowUp /> : <ArrowDown />}</span>
                  </button>

                  {dropdownOpen && (
                    <div className="absolute z-10 mt-2 w-full rounded-lg bg-white border border-gray-200 shadow-lg">
                      {orientationOptions.map((option) => (
                        <div
                          key={option.value}
                          onClick={() => {
                            setOrientation(option.value);
                            setDropdownOpen(false);
                            setOrientationError(null);
                            setUploadedImage(null);
                            setFileFormat("");
                            setUploadErrorMessage(null);
                            if (fileInputRef.current) {
                              fileInputRef.current.value = "";
                            }
                          }}
                          className={`px-4 py-2 cursor-pointer rounded-md mx-1 my-1 hover:bg-blue-50 text-sm ${
                            orientation === option.value
                              ? "bg-blue-100 font-medium"
                              : "text-gray-700"
                          }`}
                        >
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}

                  {orientation && (
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>Dimension</span>
                      <span>
                        {
                          orientationOptions.find(
                            (o) => o.value === orientation
                          )?.dimension
                        }
                      </span>
                    </div>
                  )}

                  {orientationError && (
                    <p className="text-xs text-[#DB2A2A] mt-1 ml-1">
                      {orientationError}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Upload image
                  </label>

                  {uploadedImage ? (
                    <div className="flex justify-center ">
                      <div className="relative inline-block group ">
                        <img
                          src={uploadedImage}
                          alt="Uploaded preview"
                          className="max-h-64 max-w-full  rounded shadow-md object-contain"
                        />
                        <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-40 rounded transition duration-200 pointer-events-none" />
                        <button
                          onClick={() => {
                            setUploadedImage(null);
                            setFileFormat("");
                            setUploadErrorMessage(null);
                            if (fileInputRef.current) {
                              fileInputRef.current.value = "";
                            }
                          }}
                          className="absolute top-1.5 right-1.5 bg-white border border-gray-300 rounded-full p-1 shadow opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4 text-gray-600" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      onDragOver={(e) => e.preventDefault()}
                      onDragEnter={() => setIsDragging(true)}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={handleDrop}
                      className={`border rounded-lg p-6 flex flex-col items-center justify-center text-sm transition ${
                        uploadErrorMessage
                          ? "bg-[#AF00001A] border border-[#DB2A2A]"
                          : isDragging
                          ? "bg-blue-50 border-blue-500 border-dashed"
                          : "bg-[#0BA6C705] border-blue-500 border-dashed"
                      }`}
                    >
                      <ImageUpload className="w-20 h-20 mb-1 text-gray-500" />
                      <p className="text-gray-500">Drag & drop image</p>

                      <div className="flex items-center w-full mt-3 mb-2">
                        <hr className="flex-grow ml-6 border-gray-300 " />
                        <span className="text-xs mx-2 text-gray-400">OR</span>
                        <hr className="flex-grow mr-6 border-gray-300 " />
                      </div>

                      <label className="cursor-pointer mt-2">
                        <span className="inline-block px-4 py-1.5 text-sm text-[#0BA6C7] border border-[#0BA6C7] rounded-md hover:bg-[#0BA6C710] transition">
                          Browse file
                        </span>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,video/mp4"
                          className="hidden"
                          ref={fileInputRef}
                          onChange={handleImageUpload}
                        />
                      </label>
                    </div>
                  )}

                  {uploadErrorMessage && (
                    <p className="text-xs text-[#DB2A2A] mt-2 ml-1">
                      {uploadErrorMessage}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-auto">
                <hr className="border-t border-gray-200 my-4" />
                <div className="flex justify-center gap-3 ">
                  <CustomButton
                    variant="outline"
                    onClick={handleCancel}
                    className="flex-1"
                  >
                    Cancel
                  </CustomButton>
                  <CustomButton
                    variant="default"
                    isActive={!!isFormValid}
                    onClick={handleUpload}
                    className="flex-1"
                  >
                    Upload
                  </CustomButton>
                </div>
              </div>
            </div>
          </ItemListPopup>
        </>
      )}
      <ToastContainer toasts={toasts} />
    </>
  );
};

export default UploadCreativeScreen;
